/**
 * Just enough music theory for the composer: modes as semitone steps, scale degrees and
 * triads. Pitches inside a key are "scale indices": 0 is the tonic, 7 the tonic an octave
 * up, -1 the leading note below it.
 */

/** Semitones of the seven degrees above the tonic. */
export type Scale = readonly [number, number, number, number, number, number, number];

export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  minor: [0, 2, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
} as const satisfies Record<string, Scale>;

export type ScaleName = keyof typeof SCALES;

/** MIDI note of a scale index in a key whose tonic is `tonic` (a MIDI note). */
export function scaleNote(tonic: number, scale: Scale, index: number): number {
  const octave = Math.floor(index / 7);
  const degree = index - octave * 7;
  return tonic + 12 * octave + (scale[degree] ?? 0);
}

/** Scale indices of the triad built on `degree` (root, third, fifth). */
export function triad(degree: number): readonly [number, number, number] {
  return [degree, degree + 2, degree + 4];
}

/** True when `index` is a note of the triad on `degree`, in any octave. */
export function isChordTone(index: number, degree: number): boolean {
  const relative = (((index - degree) % 7) + 7) % 7;
  return relative === 0 || relative === 2 || relative === 4;
}

/** The chord tone closest to `index` (ties go up, which keeps melodies bright). */
export function nearestChordTone(index: number, degree: number): number {
  for (let distance = 0; distance <= 3; distance++) {
    if (isChordTone(index + distance, degree)) return index + distance;
    if (isChordTone(index - distance, degree)) return index - distance;
  }
  return index;
}

/** Places a pitch class into the register starting at `low` (MIDI, inclusive, 12 notes). */
export function inRegister(pitchClass: number, low: number): number {
  return low + ((((pitchClass - low) % 12) + 12) % 12);
}
